import axios from "axios";
import { useAuth } from "@/components/AuthContext";

// 本番環境と開発環境でのベースURL自動判定
const getBaseURL = () => {
  // 開発環境
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE_URL || "http://api.joybase.localhost:5000/api";
  }

  // 本番環境
  const currentHost = window.location.hostname;
  if (currentHost.includes('joybase.jp')) {
    return "https://api.joybase.jp/api";
  }

  // フォールバック
  return import.meta.env.VITE_API_BASE_URL || "https://api.joybase.jp/api";
};

const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// デバッグ用ログ
console.log('API Base URL:', apiClient.defaults.baseURL);

// トークンの取得関数
const getToken = () => localStorage.getItem("authToken");

// JoyBase のドメインかどうかを判定
const isJoybaseDomain = (hostname: string) => {
  return ['.joybase.jp', '.stg-joybase.jp', '.joybase.localhost'].some(d => hostname.endsWith(d))
    || hostname === 'localhost';
};

// admin アプリかどうか（data-app="admin" マーカーで判定）
const isAdminApp = () => {
  return document.getElementById('react-root')?.dataset.app === 'admin';
};

// 現在のサブドメインを取得（JoyBase ドメインの場合のみ）
const getCurrentSubdomain = () => {
  const hostname = window.location.hostname;
  // 独自ドメインの場合はサブドメインとして扱わない
  if (!isJoybaseDomain(hostname)) return null;
  // admin アプリからの API 呼び出しではサブドメインは不要
  // （admin サブドメインは難読化されている場合があるため、ハードコードしない）
  if (isAdminApp()) return null;
  // localhost の場合: subdomain.joybase.localhost
  // 本番の場合: subdomain.joybase.jp
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // api, www, dashboard は除外
    if (!['api', 'www', 'dashboard'].includes(subdomain)) {
      return subdomain;
    }
  }
  return null;
};

// リクエスト時にトークンとサブドメインを追加
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  // サイト特定用ヘッダーを追加
  const hostname = window.location.hostname;
  const subdomain = getCurrentSubdomain();
  if (subdomain) {
    config.headers['X-Site-Subdomain'] = subdomain;
  } else if (!isJoybaseDomain(hostname)) {
    // カスタムドメインの場合はホスト名全体を送信
    config.headers['X-Custom-Domain'] = hostname;
  }

  return config;
});

// レスポンス時にトークン期限切れやエラーを確認
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      const errorData = error.response.data;
      localStorage.removeItem("authToken");
      localStorage.removeItem("currentUser");

      window.location.href = "/login";
    }

    // その他のエラーはそのままスロー
    return Promise.reject(error);
  }
);
export default apiClient;
